import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import './TS_Dialog.scss';
import { _values, BadImplementationException, filterInstances, flatArray } from '@nu-art/ts-common';
import { ModuleFE_Dialog } from '../../component-modules/ModuleFE_Dialog.js';
import { TS_ErrorBoundary } from '../TS_ErrorBoundary/index.js';
import { Button } from '../Button/Button.js';
import { LL_V_L } from '../Layouts/index.js';
import { ComponentSync } from '../../core/ComponentSync.js';
import { _className, stopPropagation } from '../../utils/tools.js';
/**
 * ##TS_Dialog
 *
 * This class defines the logic and render behavior for dialogs in the system.
 * Any dialog class in the system is meant to inherit this class to utilize its features.
 */
export class TS_Dialog extends ComponentSync {
    // ######################## Life Cycle ########################
    __consumeDialogCloseEvent(dialogKey) {
        return false;
    }
    componentDidMount() {
        this._keyActionMapCreator();
        const dialog = document.getElementById(this.props.dialogId);
        dialog?.focus();
        this.forceUpdate();
    }
    // ######################## KeyMap ########################
    /**
     * A map held per instance connecting a (keyboard)key to a dialog button.
     * This map is filled in by calling _keyActionMapCreator with buttons.
     * @protected
     */
    keyActionMap = {};
    /**
     * A function to fill in keyActionMap.
     *
     * This function receives an array of buttons and for each button will connect any associated
     * keys to the button, to be used by dialogKeyEventHandler.
     *
     * Keep in mind:
     *
     * 1. If an associated key is found in more than one button, an error will be thrown.
     *
     * 2. This function must be called in the constructor of the inheriting class.
     *
     * @param buttons - an array of buttons of type DialogButton
     */
    _keyActionMapCreator = () => {
        flatArray(filterInstances(_values(this.buttons()))).forEach(button => {
            if (!button.associatedKeys?.length)
                return;
            const ref = React.createRef();
            button.associatedKeys.forEach(key => {
                if (this.keyActionMap[key])
                    throw new BadImplementationException(`Trying to assign action to key ${key} more than once`);
                this.keyActionMap[key] = ref;
            });
        });
    };
    deriveStateFromProps(nextProps, state) {
        state.dialogIsBusy ??= false;
        return state;
    }
    dialogKeyEventHandler = (e) => {
        e.persist();
        if (e.key === 'Escape' && !this.state.dialogIsBusy)
            this.closeDialog();
        this.keyActionMap[e.key]?.current?.click(e);
    };
    // ######################## Utils ########################
    _buttonsCreator = (buttons) => {
        if (!buttons?.length)
            return undefined;
        return _jsx(_Fragment, { children: buttons.map((button, i) => {
                const ref = button.associatedKeys ? this.keyActionMap[button.associatedKeys[0]] : undefined;
                return (button.renderer ?? TS_Dialog.button)(button, i, ref);
            }) });
    };
    closeDialog = (force = false) => {
        if (this.state.error)
            this.logError(this.state.error);
        ModuleFE_Dialog.close(force, this.props.dialogKey);
    };
    // ######################## Render - Header ########################
    dialogHeader = (headerContent) => {
        return headerContent && _jsx("div", { className: 'ts-dialog__header', children: headerContent });
    };
    renderHeader = () => {
        return undefined;
    };
    // ######################## Render - Main ########################
    dialogBody = (mainContent) => {
        return mainContent && _jsx("div", { className: 'ts-dialog__main', children: mainContent });
    };
    renderBody = () => {
        return undefined;
    };
    // ######################## Render - Buttons ########################
    dialogButtons = (buttons) => {
        if (_values(buttons).every(arr => !arr || !arr.length))
            return undefined;
        return _jsxs("div", { className: 'ts-dialog__buttons', children: [buttons.left && _jsx("div", { className: 'ts-dialog__buttons__left', children: this._buttonsCreator(buttons.left) }), buttons.center &&
                    _jsx("div", { className: 'ts-dialog__buttons__center', children: this._buttonsCreator(buttons.center) }), buttons.right && _jsx("div", { className: 'ts-dialog__buttons__right', children: this._buttonsCreator(buttons.right) })] });
    };
    buttons = () => {
        return {};
    };
    performAction = (action) => {
        this.setState({ dialogIsBusy: true }, async () => {
            await action();
            if (this.mounted)
                this.setState({ dialogIsBusy: false });
        });
    };
    // ######################## Render ########################
    render() {
        let buttons = {};
        let mainContent;
        let headerContent;
        try {
            buttons = this.buttons();
            mainContent = this.renderBody();
            headerContent = this.renderHeader();
        }
        catch (err) {
            if (!this.state.error)
                this.setState({ error: err });
        }
        return _jsx(TS_ErrorBoundary, { error: this.state.error, onClick: () => this.closeDialog(true), children: _jsxs(LL_V_L, { className: _className('ts-dialog', this.props.className), id: this.props.dialogId, tabIndex: -1, onKeyDown: this.dialogKeyEventHandler, onClick: stopPropagation, onContextMenu: stopPropagation, children: [this.dialogHeader(headerContent), this.dialogBody(mainContent), this.dialogButtons(buttons)] }) });
    }
    static button = (button, index, ref) => {
        return _jsx(Button, { className: button.className, variant: button.variant, innerRef: ref, onClick: button.onClick, disabled: button.disabled, onDisabledClick: button.onDisabledClick, children: button.content }, `button-${index}`);
    };
    static Button_Cancel = {
        content: 'Cancel',
        onClick: () => ModuleFE_Dialog.close(true),
        associatedKeys: ['Escape']
    };
}
//# sourceMappingURL=TS_Dialog.js.map